import { useContext, useContextSelector } from 'use-context-selector';
import { AxiosError } from 'axios';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { FavouriteTransfersContext } from './FavouriteTransfersContext';

const useFavouriteTransfersState = () => {
  const context = useContext(FavouriteTransfersContext);

  if (context === undefined) {
    throw new Error('useFavouriteTransfersState must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useFavouriteTransfers = () => {
  const context = useContextSelector(
    FavouriteTransfersContext,
    state => state.favouriteTransfersListData?.favoriteTransfersList || [],
  );

  if (context === undefined) {
    throw new Error('useFavouriteTransfers must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useFavouriteTransfersTotalResults = () => {
  const context = useContextSelector(
    FavouriteTransfersContext,
    state => state.favouriteTransfersListData?.totalResults ?? 0,
  );

  if (context === undefined) {
    throw new Error('useFavouriteTransfersTotalResults must be used within FavouriteTransfersContext');
  }

  return context;
};

const useFavouriteTransferErrorState = (
  errorProperty:
    | 'favouriteTransfersDataError'
    | 'editFavouriteTransferError'
    | 'deleteFavouriteTransferError'
    | 'currenciesError',
): AxiosError<APIGatewayResponse<unknown>, any> | undefined => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.errorState);

  if (context === undefined) {
    throw new Error('useFavouriteTransferErrorState must be used within FavouriteTransfersProvider');
  }

  return context[errorProperty];
};

const useFavouriteTransferLoadingState = (
  loadingProperty:
    | 'isLoadingFavouriteTransfersList'
    | 'isLoadingEditFavouriteTransfer'
    | 'isLoadingDeleteFavouriteTransfer'
    | 'isLoadingCurrencies',
) => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.loadingState);

  if (context === undefined) {
    throw new Error('useFavouriteTransferLoadingState  must be used within FavouriteTransfersProvider');
  }

  return context[loadingProperty];
};

const useSelectedFavouriteTransfer = () =>
  useContextSelector(FavouriteTransfersContext, state => state.selectedFavouriteTransfer);

const useSelectedFavouriteTransferAction = () => useContextSelector(FavouriteTransfersContext, state => state.action);

const useOnFavouriteTransferAction = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onFavouriteTransferAction);

  if (context === undefined) {
    throw new Error('useOnFavouriteTransferAction must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useEditFavouriteTransferData = () =>
  useContextSelector(FavouriteTransfersContext, state => state.editFavouriteTransferData);

const useDeleteFavouriteTransferData = () =>
  useContextSelector(FavouriteTransfersContext, state => state.deleteFavouriteTransferData);

const useOnEditFavouriteTransfer = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onEditFavouriteTransfer);

  if (context === undefined) {
    throw new Error('useOnEditFavouriteTransfer must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useOnDeleteFavouriteTransfer = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onDeleteFavouriteTransfer);

  if (context === undefined) {
    throw new Error('useOnDeleteFavouriteTransfer must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useOnShareFavouriteTransfer = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onShareFavouriteTransfer);

  if (context === undefined) {
    throw new Error('useOnShareFavouriteTransfer must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useOnUseTemplateFavouriteTransfer = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onUseTemplateFavouriteTransfer);

  if (context === undefined) {
    throw new Error('useOnUseTemplateFavouriteTransfer must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useFavouriteTransferListFilters = () =>
  useContextSelector(FavouriteTransfersContext, state => state.favouriteTransfersListFilters);

const useOnFavouriteTransferListFiltersChange = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onFavouriteTransferListFiltersChange);

  if (context === undefined) {
    throw new Error('useOnFavouriteTransferListFiltersChange must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useOnToggleFavouriteTransfersFilterDrawer = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.toggleFavouriteTransfersFilterDrawer);

  if (context === undefined) {
    throw new Error('useOnToggleFavouriteTransfersFilterDrawer must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useSetFavouriteTransfersFilterDrawerClose = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.setFavouriteTransfersFilterDrawerClose);

  if (context === undefined) {
    throw new Error('useSetFavouriteTransfersFilterDrawerClose must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useIsFavouriteTransfersFilterDrawerOpen = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.isFavouriteTransfersFilterDrawerOpen);

  if (context === undefined) {
    throw new Error('useIsFavouriteTransfersFilterDrawerOpen must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useCurrencies = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.currencies);

  if (context === undefined) {
    throw new Error('useCurrencies must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useOnResetSelectedFavouriteTransfer = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onResetSelectedFavouriteTransfer);

  if (context === undefined) {
    throw new Error('useOnResetSelectedFavouriteTransfer must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useOnResetFavouriteTransferListFilters = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.onResetFavouriteTransferListFilters);

  if (context === undefined) {
    throw new Error('useOnResetFavouriteTransferListFilters must be used within FavouriteTransfersProvider');
  }

  return context;
};

const useFavouriteTransfersRefetch = () => {
  const context = useContextSelector(FavouriteTransfersContext, state => state.favouriteTransfersRefetch);

  if (context === undefined) {
    throw new Error('useFavouriteTransfersRefetch must be used within FavouriteTransfersProvider');
  }

  return context;
};

export {
  useFavouriteTransfersState,
  useFavouriteTransferListFilters,
  useFavouriteTransfers,
  useFavouriteTransfersTotalResults,
  useFavouriteTransferErrorState,
  useFavouriteTransferLoadingState,
  useSelectedFavouriteTransferAction,
  useOnFavouriteTransferAction,
  useSelectedFavouriteTransfer,
  useEditFavouriteTransferData,
  useDeleteFavouriteTransferData,
  useOnEditFavouriteTransfer,
  useOnShareFavouriteTransfer,
  useOnDeleteFavouriteTransfer,
  useOnUseTemplateFavouriteTransfer,
  useOnFavouriteTransferListFiltersChange,
  useOnToggleFavouriteTransfersFilterDrawer,
  useSetFavouriteTransfersFilterDrawerClose,
  useIsFavouriteTransfersFilterDrawerOpen,
  useCurrencies,
  useOnResetSelectedFavouriteTransfer,
  useOnResetFavouriteTransferListFilters,
  useFavouriteTransfersRefetch,
};
